import React from 'react';
import { Field, useFormikContext } from 'formik';
import {
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
    SelectChangeEvent,
} from '@mui/material';
import { Info } from '@mui/icons-material';
import { ComboBox, LegalEntityIdField, VATField } from '@fnol/library/src/components';
import {
    constValues,
    personType,
    question50MaxLength,
    requestType,
    translations,
    questionMaxLength,
} from '../../lib/consts';

type Props = {
    legalEntityType: personType;
};

const LegalEntity = ({ legalEntityType }: Props) => {
    const { values, errors, touched, handleChange, setFieldValue } = useFormikContext<any>();
    const name = `${personType[legalEntityType]}LegalEntityName`;
    const id = `${personType[legalEntityType]}LegalEntityID`;
    const vat = `${personType[legalEntityType]}LegalEntityVAT`;
    const country = `${personType[legalEntityType]}LegalEntityCountry`;
    const countryText = `${personType[legalEntityType]}LegalEntityMyCountry`;
    const city = `${personType[legalEntityType]}LegalEntityCity`;
    const cityInSerbia = `${personType[legalEntityType]}LegalEntityCityInSerbia`;
    const street = `${personType[legalEntityType]}LegalEntityStreet`;
    const email = `${personType[legalEntityType]}LegalEntityEmail`;
    const duplicatedId = `${personType[legalEntityType]}DuplicatedId`;

    // selected country textual representation
    const selectedLegalEntityCountry = values.preparedCountries?.find(
        (item: any) => item.id === parseInt(values[country] as string),
    );
    values[countryText] = selectedLegalEntityCountry?.text;

    const handleChangeCountry = (event: SelectChangeEvent<HTMLSelectElement>) => {
        setFieldValue(country, event.target.value);
        setFieldValue(street, '');
        setFieldValue(city, '');
        setFieldValue(cityInSerbia, '');
    };

    const checkIfIdExists = () => {
        if (values.requestType === requestType.Both || values.requestType === requestType.MaterialDamage) {
            const insertTypes: number[] = [1, 2, 3, 4, 5, 6];
            const insertedIds: string[] = [];

            insertTypes.map((insertType) => {
                insertedIds.push(values[`${personType[insertType]}LegalEntityID`]);
            });

            const duplicatedIdExists = insertedIds.some((idValue) => {
                if (idValue && idValue !== '') {
                    return insertedIds.indexOf(idValue) !== insertedIds.lastIndexOf(idValue);
                }
            });

            setFieldValue(duplicatedId, duplicatedIdExists);
        }
    };

    return (
        <>
            <FormControl>
                <Field
                    name={name}
                    label={translations.legalInfo.SUBMITTER_LEGAL_NAME}
                    as={TextField}
                    inputProps={{
                        maxLength: question50MaxLength,
                    }}
                    error={!!(touched[name] && errors[name])}
                    required
                />
                {touched[name] && errors[name] ? (
                    <FormHelperText error={true}>
                        <>{errors[name]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl>
                <InputLabel id="country-label">{translations.COUNTRY}</InputLabel>

                <Select
                    id={country}
                    labelId="country-label"
                    name={country}
                    value={values[country] || ''}
                    label={translations.COUNTRY}
                    error={!!(touched[country] && errors[country])}
                    onChange={handleChangeCountry}
                >
                    {values.countries?.map((option: any) => {
                        return (
                            <MenuItem key={option.id} value={option.id}>
                                {option.text}
                            </MenuItem>
                        );
                    })}
                </Select>
            </FormControl>

            {values[country] !== '197' && values[country] !== 197 ? (
                <FormControl>
                    <Field
                        name={city}
                        label={translations.CITY}
                        as={TextField}
                        inputProps={{
                            maxLength: question50MaxLength,
                        }}
                        error={!!(touched[city] && errors[city])}
                    />
                    {touched[city] && errors[city] ? (
                        <FormHelperText error={true}>
                            <>{errors[city]}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
            ) : (
                <FormControl error={!!(touched[cityInSerbia] && errors[cityInSerbia])}>
                    <Field
                        name={cityInSerbia}
                        label={translations.CITY}
                        options={
                            values.cities || [
                                {
                                    id: '',
                                    value: '',
                                    text: '',
                                    code: '',
                                },
                            ]
                        }
                        onChange={handleChange}
                        defaultValue={values[cityInSerbia]}
                        as={ComboBox}
                        error={!!(touched[cityInSerbia] && errors[cityInSerbia])}
                    />
                    {touched[cityInSerbia] && errors[cityInSerbia] ? (
                        <FormHelperText error={true}>
                            <>{errors[cityInSerbia]}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
            )}

            <FormControl>
                <Field
                    name={street}
                    label={translations.STREET}
                    as={TextField}
                    inputProps={{
                        maxLength: question50MaxLength,
                    }}
                    error={!!(touched[street] && errors[street])}
                />
                {touched[street] && errors[street] ? (
                    <FormHelperText error={true}>
                        <>{errors[street]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl>
                <Field
                    name={id}
                    label={translations.legalInfo.ID}
                    as={LegalEntityIdField}
                    onBlur={checkIfIdExists}
                    selectedCountry={values[country]}
                    error={!!(touched[id] && errors[id])}
                    required
                />
                {touched[id] && errors[id] ? (
                    <FormHelperText error={true}>
                        <>{errors[id]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            {values[duplicatedId] ? (
                <Stack direction="row" alignItems="top" spacing={2} marginTop={2} marginBottom={3}>
                    <Info color="primary" />
                    <Typography variant="body2" paddingTop={0.5}>
                        {translations.validationMessages.ALREADY_HAS_IMMATERIAL}
                    </Typography>
                </Stack>
            ) : null}

            <FormControl>
                <Field
                    name={vat}
                    label={translations.legalInfo.VAT}
                    as={VATField}
                    selectedCountry={values[country]}
                    error={!!(touched[vat] && errors[vat])}
                    required
                />
                {touched[vat] && errors[vat] ? (
                    <FormHelperText error={true}>
                        <>{errors[vat]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl>
                <Field
                    name={email}
                    label={translations.legalInfo.EMAIL}
                    type="email"
                    as={TextField}
                    inputProps={{
                        maxLength: questionMaxLength,
                    }}
                    error={!!(touched[email] && errors[email])}
                />
                {touched[email] && errors[email] ? (
                    <FormHelperText error={true}>
                        <>{errors[email]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>
        </>
    );
};

export const LegalEntityCleanup = (
    type: personType,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void,
) => {
    setFieldValue(`${personType[type]}LegalEntityName`, '');
    setFieldValue(`${personType[type]}LegalEntityID`, '');
    setFieldValue(`${personType[type]}LegalEntityVAT`, '');
    setFieldValue(`${personType[type]}LegalEntityCountry`, constValues.countrySerbiaId);
    setFieldValue(`${personType[type]}LegalEntityCity`, '');
    setFieldValue(`${personType[type]}LegalEntityCityInSerbia`, '');
    setFieldValue(`${personType[type]}LegalEntityStreet`, '');
    setFieldValue(`${personType[type]}LegalEntityEmail`, '');
};

export default LegalEntity;
